import { ButtonV2, Form } from 'components';
import { ModalControllerStepsContext } from 'hocs/withContext';
import Program from 'models/Program';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import {
  programsDefaultSchedule,
  programSessionsToSchedule,
  scheduleToProgramSessions,
  TimePeriod
} from 'transforms/schedule';
import './style.scss';

interface Step1Props {
  program: Program;
  onUpdateProgram: (updatedProgram: Program) => void;
  onClose: (program: Program | null) => void;
}

interface FormValues {
  name?: string;
  starts_at?: string;
  ends_at?: string;
  schedule?: any; // Replace `any` with the actual type if known
  min_age_yrs?: number;
  min_age_mos?: number;
  max_age_yrs?: number;
  max_age_mos?: number;
  enabled?: boolean;
}

const Step1: React.FC<Step1Props> = ({ program, onUpdateProgram, onClose }) => {
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>();
  const [isInvalidDateRange, setIsInvalidDateRange] = useState(false);
  const [isAnyScheduled, setIsAnyScheduled] = useState(true);
  const { nextStep } = useContext(ModalControllerStepsContext);
  const isSubmitDisabled = !isAnyScheduled || !formValues?.name?.length;

  const ageInMonths = (yrs: number, mos: number) => Number(yrs || 0) * 12 + Number(mos || 0);
  const ageInYearsMonths = (mos: number) => [Math.floor(mos / 12), mos % 12];

  const submit = async (values: FormValues) => {
    const {
      name = '',
      starts_at = '',
      ends_at = '',
      schedule,
      min_age_yrs,
      min_age_mos,
      max_age_yrs,
      max_age_mos,
      enabled = true
    } = values;

    // silently exiting from submission when there are custom errors
    if (isInvalidDateRange || !isAnyScheduled) {
      return;
    }

    const lowerAgeInMonths = ageInMonths(Number(min_age_yrs), Number(min_age_mos));
    const upperAgeInMonths = ageInMonths(Number(max_age_yrs), Number(max_age_mos));

    if (lowerAgeInMonths > upperAgeInMonths && upperAgeInMonths !== 0) {
      Actions.showFlash('Lower limit should be less than upper limit', 'alert');
      return;
    }

    const payload: { program: Program } = {
      program: {
        name,
        starts_at: new Date(starts_at),
        ends_at: new Date(ends_at),
        enabled,
        registration_capacity: program?.registration_capacity || 0,
        ratio_numerator: program?.ratio_numerator || 0,
        program_sessions_attributes: scheduleToProgramSessions(schedule),
        lower_age_in_months: lowerAgeInMonths,
        upper_age_in_months: upperAgeInMonths,
        standalone: program?.standalone || false
      }
    };

    try {
      setIsLoading(true);
      const response = await req.validateProgram(payload);
      const updatedProgram = {
        ...program,
        ...response.program,

        // Backend passes back an empty sections array. There's a separate endpoint for adding these.
        sections: program?.sections || []
      };
      onUpdateProgram(updatedProgram);
      nextStep();
    } catch (err) {
      setIsLoading(false);
      const formErrors = (err as any).response?.data?.form_errors;
      if (formErrors) {
        setErrors(formErrors);
      } else {
        Actions.reportError('There was problem creating a program', err as Error);
      }
    }
  };

  const updateFormValues = (values: FormValues) => {
    const invalidDateRange =
      !!values.starts_at && !!values.ends_at && moment(values.starts_at) > moment(values.ends_at);

    const scheduleEntries = Object.entries<TimePeriod>(values.schedule || {});
    const foundEntries = scheduleEntries.filter(([, ampm]) => ampm.am || ampm.pm);
    const length = foundEntries.length;

    const anyScheduled = length > 0;

    setFormValues(values);
    setErrors({
      ...errors,
      starts_at: invalidDateRange ? ['Start date must be before end date'] : null,
      ends_at: invalidDateRange ? ['End date must be after start date'] : null,
      schedule: anyScheduled ? null : ['Schedule is required']
    });
    setIsInvalidDateRange(invalidDateRange);
    setIsAnyScheduled(anyScheduled);
  };

  const [min_age_yrs, min_age_mos] = ageInYearsMonths(Number(program?.lower_age_in_months));
  const [max_age_yrs, max_age_mos] = ageInYearsMonths(Number(program?.upper_age_in_months));
  const defaultSchedule = program?.program_sessions?.length
    ? programSessionsToSchedule(program?.program_sessions)
    : programsDefaultSchedule;

  return (
    <div className="modal__wrapper">
      <div className="modal__header">
        <span className="modal__header-title">Create Program</span>
        <span className="modal__header-steps">
          <span>Step 1</span> / 2
        </span>
      </div>

      <div className="modal__container">
        <Form
          validateOn="submit"
          onSubmit={submit}
          errors={errors}
          isLoading={isLoading}
          onInit={updateFormValues}
          onChange={updateFormValues}
        >
          <div className="form__row">
            <Form.Input
              name="name"
              label="Program Name"
              placeholder="Enter Unique Program Name"
              defaultValue={program?.name}
              required
              asterisk
            />
          </div>

          <div className="form__row">
            <Form.DatePicker
              label="Start Date"
              name="starts_at"
              className="create-program__date"
              required={Boolean(formValues?.ends_at)}
              asterisk={Boolean(formValues?.ends_at)}
              defaultValue={program?.starts_at ? moment(program?.starts_at).toDate() : null}
            />
            <Form.DatePicker
              label="End Date"
              name="ends_at"
              className="create-program__date"
              defaultValue={program?.ends_at ? moment(program?.ends_at).toDate() : null}
            />
          </div>

          <div className="form__row">
            <Form.ScheduleSelect
              name="schedule"
              defaultValue={defaultSchedule}
              layout="inline"
              required
              asterisk
              label="Weekly Schedule"
            />
          </div>

          <div className="form__row">
            <div>
              <div className="form__label">Age Range</div>
              <div className="create-program-v2__age">
                <div>Minimum age</div>
                <Form.Input
                  name="min_age_yrs"
                  data-cy="min_age_yrs"
                  type="number"
                  min={0}
                  max={99}
                  placeholder=""
                  defaultValue={min_age_yrs !== undefined ? min_age_yrs : ''}
                />
                <div>yr(s)</div>
                <Form.Input
                  name="min_age_mos"
                  data-cy="min_age_mos"
                  type="number"
                  min={0}
                  max={11}
                  placeholder=""
                  defaultValue={min_age_mos !== undefined ? min_age_mos : ''}
                />
                <div>mo(s)</div>
                <div>Maximum age</div>
                <Form.Input
                  name="max_age_yrs"
                  data-cy="max_age_yrs"
                  type="number"
                  min={0}
                  max={99}
                  placeholder=""
                  defaultValue={max_age_yrs !== undefined ? max_age_yrs : ''}
                />
                <div>yr(s)</div>
                <Form.Input
                  name="max_age_mos"
                  data-cy="max_age_mos"
                  type="number"
                  min={0}
                  max={11}
                  placeholder=""
                  defaultValue={max_age_mos !== undefined ? max_age_mos : ''}
                />
                <div>mo(s)</div>
              </div>
            </div>
          </div>

          <div className="form__row">
            <div>
              <label className="form__label" htmlFor="enabled">
                Status
              </label>
              <div>
                <Form.Checkbox
                  className="mr-40"
                  type="slider"
                  name="enabled"
                  label="Active"
                  defaultValue={program?.enabled}
                />
              </div>
            </div>
          </div>

          <div className="form__row form__row--actions">
            <ButtonV2 data-cy="cancel" secondary label="Cancel" onClick={() => onClose(null)} />
            <Form.Submit data-cy="submit" label="Continue" disabled={isLoading || isSubmitDisabled} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Step1;
